import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Clock, MapPin, Plane, ArrowLeft, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import getApiUrl from "@/utils/api";

const API = getApiUrl();

export default function ReschedulePage() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ new_travel_date: "", new_travel_time: "", reason: "" });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/bookings/${bookingId}/reschedule-info`, { params: { token } });
        setBooking(res.data);
        setForm(f => ({ ...f, new_travel_date: res.data.travel_date || "", new_travel_time: res.data.travel_time || "" }));
      } catch (e) {
        setError(e.response?.data?.detail || "Unable to load booking. The link may be invalid or expired.");
      } finally {
        setLoading(false);
      }
    };
    if (bookingId && token) load();
    else { setError("Missing booking or token."); setLoading(false); }
  }, [bookingId, token]);

  const handleSubmit = async () => {
    if (!form.new_travel_date) { toast.error("Please pick a new date"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/reschedule`, form, { params: { token } });
      toast.success("Trip rescheduled!");
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reschedule");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100" data-testid="reschedule-loading">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-rose-100 p-4">
        <Card className="max-w-md w-full" data-testid="reschedule-error">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-2">Cannot Reschedule</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Link to="/"><Button variant="outline" className="rounded-full"><ArrowLeft className="w-4 h-4 mr-2" /> Home</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 p-4">
        <Card className="max-w-md w-full" data-testid="reschedule-success">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-3" />
            <h2 className="text-2xl font-bold mb-2">Trip Rescheduled!</h2>
            <p className="text-muted-foreground mb-2">Your trip is now scheduled for:</p>
            <p className="text-lg font-bold text-green-700">{form.new_travel_date} {form.new_travel_time && `at ${form.new_travel_time}`}</p>
            <p className="text-xs text-muted-foreground mt-3">A confirmation has been sent to your WhatsApp.</p>
            <Link to="/check-status" className="block mt-5"><Button className="rounded-full bg-green-600 hover:bg-green-700">View Booking Status</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-10 px-4">
      <div className="max-w-xl mx-auto">
        <Card className="shadow-xl border-0" data-testid="reschedule-card">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="bg-green-600 rounded-xl p-2.5"><Calendar className="w-6 h-6 text-white" /></div>
              <div>
                <h1 className="text-2xl font-bold">Reschedule Your Trip</h1>
                <p className="text-sm text-muted-foreground">Easy Transportation</p>
              </div>
            </div>

            {/* Current trip details */}
            <div className="bg-gray-50 rounded-xl p-4 mb-5 space-y-2 text-sm" data-testid="current-trip-info">
              <p><span className="font-semibold">Passenger:</span> {booking.full_name}</p>
              <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-gray-500" /> Currently: <span className="font-semibold">{booking.travel_date} {booking.travel_time}</span></p>
              <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-500" /> {booking.pickup_location} → {booking.dropoff_location}</p>
              {booking.flight_number && <p className="flex items-center gap-2"><Plane className="w-4 h-4 text-gray-500" /> {booking.airline} {booking.flight_number}</p>}
              {booking.rescheduled_count > 0 && <p className="text-xs text-amber-700">⚠️ Rescheduled {booking.rescheduled_count} time(s) already (max 3)</p>}
            </div>

            {/* New date & time */}
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">New Travel Date *</Label>
                <Input
                  type="date"
                  value={form.new_travel_date}
                  onChange={(e) => setForm(f => ({ ...f, new_travel_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="h-12 rounded-xl mt-1"
                  data-testid="new-date-input"
                />
              </div>
              <div>
                <Label className="font-semibold">New Travel Time</Label>
                <Input
                  type="time"
                  value={form.new_travel_time}
                  onChange={(e) => setForm(f => ({ ...f, new_travel_time: e.target.value }))}
                  className="h-12 rounded-xl mt-1"
                  data-testid="new-time-input"
                />
              </div>
              <div>
                <Label className="font-semibold">Reason (optional)</Label>
                <Input
                  type="text"
                  placeholder="e.g. flight delayed, plans changed..."
                  value={form.reason}
                  onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}
                  className="h-12 rounded-xl mt-1"
                  data-testid="reschedule-reason"
                />
              </div>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={submitting || !form.new_travel_date}
              className="w-full mt-6 h-12 rounded-full bg-green-600 hover:bg-green-700 font-semibold"
              data-testid="reschedule-submit-btn"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Clock className="w-4 h-4 mr-2" /> Confirm Reschedule</>}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-3">Need help? Call (815) 914-8139</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
